/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useBirthPreparedness } from "./hooks/useBirthPreparedness";
import { BirthPreparednessForm } from "./BirthPreparednessForm";
import { DangerSignsPanel } from "./DangerSignsPanel";
import { history } from "../../../../Helpers/history";
import {
    BPPageWrapper,
    BPHeader,
    BPHeaderTitle,
    BPHeaderStats,
    StatBadge,

    EmptyState,
    GoToBookingBtn,
    LoadingWrapper,
} from "./BirthPreparednessStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";

/**
 * BirthPreparednessPage — Page-level container for Birth Preparedness & Danger Signs
 *
 * Route: /gyn-and-obs-birth-preparedness/:patient_number/:visit_id/:patient_type
 */
const BirthPreparednessPage = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── Birth Preparedness hook ─── */
    const bp = useBirthPreparedness(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    return (
        <div className="journals">
            <PageTitle title="Birth Preparedness & Danger Signs" />
            <SubHeader
                title="OB/GYN"
                subTitle="Birth Preparedness & Danger Signs"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="ANC"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={snackbar.type}
                open={snackbar.openBar}
                message={snackbar.message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <BPPageWrapper>
                    {/* ═══ PATIENT INFO ═══ */}
                    <ObGynPatientInfo
                        patient_number={patient_number}
                        visit_id={visit_id}
                    />

                    {/* ═══ LOADING STATE ═══ */}
                    {bp.loadingBooking && (
                        <LoadingWrapper>
                            <span>⏳</span> Loading patient ANC data...
                        </LoadingWrapper>
                    )}

                    {/* ═══ NO BOOKING STATE ═══ */}
                    {!bp.loadingBooking && !bp.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            <EmptyState>
                                <div className="empty-icon">🤰</div>
                                <div className="empty-title">No Active ANC Booking Found</div>
                                <div className="empty-desc">
                                    This patient must be registered in the Antenatal Care program
                                    before birth preparedness can be documented. The ANC booking links
                                    the birth plan to the current pregnancy.
                                </div>
                                <GoToBookingBtn onClick={goToBooking}>
                                    Go to ANC Registration & Booking
                                </GoToBookingBtn>
                            </EmptyState>
                        </div>
                    )}

                    {/* ═══ MAIN CONTENT ═══ */}
                    {!bp.loadingBooking && bp.bookingData && (
                        <div style={{ marginTop: "16px" }}>
                            {/* ─── Header ─── */}
                            <BPHeader>
                                <BPHeaderTitle>
                                    <h3>🤰 Birth Preparedness & Danger Signs</h3>
                                    <p>
                                        Preparation plan and danger sign monitoring
                                        {bp.gestationalAge !== null
                                            ? " · GA: " + bp.gestationalAge + " weeks"
                                            : ""}
                                    </p>
                                </BPHeaderTitle>
                                <BPHeaderStats>
                                    <StatBadge>
                                        📋 Plan <span className="count">{bp.completionStats.overallPercent}%</span>
                                    </StatBadge>
                                    <StatBadge>
                                        ✓ Items <span className="count">
                                            {bp.completionStats.itemsDone}/{bp.completionStats.itemsTotal}
                                        </span>
                                    </StatBadge>
                                    {bp.activeDangerSigns.length > 0 && (
                                        <StatBadge style={{ background: "rgba(229,57,53,0.3)" }}>
                                            🚨 Alerts <span className="count">{bp.activeDangerSigns.length}</span>
                                        </StatBadge>
                                    )}
                                </BPHeaderStats>
                            </BPHeader>

                            {/* ─── Tab Bar ─── */}
                            <ObGynTabBar>
                                <ObGynTabBtn
                                    active={bp.activeTab === "preparedness"}
                                    onClick={function() { bp.setActiveTab("preparedness"); }}
                                >
                                    📋 Birth Preparedness
                                    <ObGynTabBadge active={bp.activeTab === "preparedness"}>{bp.completionStats.overallPercent}%</ObGynTabBadge>
                                </ObGynTabBtn>
                                <ObGynTabBtn
                                    active={bp.activeTab === "danger"}
                                    onClick={function() { bp.setActiveTab("danger"); }}
                                >
                                    ⚠ Danger Signs
                                    {bp.activeDangerSigns.length > 0 && (
                                        <ObGynTabBadge active={bp.activeTab === "danger"}>{bp.activeDangerSigns.length}</ObGynTabBadge>
                                    )}
                                </ObGynTabBtn>
                            </ObGynTabBar>

                            {/* ─── Tab Content ─── */}
                            {bp.activeTab === "preparedness" && (
                                <BirthPreparednessForm
                                    prepForm={bp.prepForm}
                                    togglePrepItem={bp.togglePrepItem}
                                    handlePrepChange={bp.handlePrepChange}
                                    toggleItemPrepared={bp.toggleItemPrepared}
                                    toggleCounselledSign={bp.toggleCounselledSign}
                                    savePreparedness={bp.savePreparedness}
                                    savingPrep={bp.savingPrep}
                                    completionStats={bp.completionStats}
                                    PREPAREDNESS_ITEMS={bp.PREPAREDNESS_ITEMS}
                                    ITEMS_TO_PREPARE={bp.ITEMS_TO_PREPARE}
                                    DANGER_SIGNS={bp.DANGER_SIGNS}
                                />
                            )}

                            {bp.activeTab === "danger" && (
                                <DangerSignsPanel
                                    dangerSigns={bp.dangerSigns}
                                    activeDangerSigns={bp.activeDangerSigns}
                                    loadingDanger={bp.loadingDanger}
                                    dangerForm={bp.dangerForm}
                                    handleDangerFormChange={bp.handleDangerFormChange}
                                    openDangerDialog={bp.openDangerDialog}
                                    closeDangerDialog={bp.closeDangerDialog}
                                    submitDangerSign={bp.submitDangerSign}
                                    savingDanger={bp.savingDanger}
                                    dangerDialog={bp.dangerDialog}
                                    DANGER_SIGNS={bp.DANGER_SIGNS}
                                />
                            )}
                        </div>
                    )}
                </BPPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(BirthPreparednessPage);
